import { React } from 'Imports';
import { FormControl, Select } from 'MaterialUIComponents';
import { IDropdownItem } from '$Components/FilterComponents/VPDropdown';
import { Box, MenuProps } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp, faCaretDown, faCaretUp, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { useEffect, useState } from 'react';
import DropdownSearchField from '$Components/FilterComponents/Filters/DropdownSearchField';
import DropdownMenuItem from '$Components/FilterComponents/Filters/DropdownMenuItem';
import { isEncompass } from '$Utilities/integrationPlatform';
import * as scssStyles from '$CSS/settings.scss';

const styles = require('$Components/FilterComponents/Filters/SelectBase.scss') as {
    dropdownLabel: string;
    dropdownLabelText: string;
    dropdownSelectedCount: string;
    hasSelectedElementsGeotab: string;
    hasSelectedElementsEncompass: string;
    noSelectedElementsGeotab: string;
    noSelectedElementsEncompass: string;
    selectIcon: string;
    noResultsMessage: string;
    scrollAreaGeotab: string;
    scrollAreaEncompass: string;
    formLabel: string;
    formLabelGeotab: string;
    requiredLabel: string;
};

interface IDropdownLabelProps {
    labelText: string;
    selectedItem: IDropdownItem | null;
    placeholder?: string;
    selectFontSize?: number;
}

const DropdownLabel = (props: IDropdownLabelProps) => {
    const displayText = props.selectedItem ? props.selectedItem.label : props.placeholder;

    return (
        <div className={styles.dropdownLabel}>
            <div
                style={{
                    ...(props.selectedItem ? {} : { color: '#878B8C' }),
                    ...(props.selectFontSize && { fontSize: `${props.selectFontSize}px` }),
                    letterSpacing: 0,
                }}
                className={styles.dropdownLabelText}
            >
                {displayText}
            </div>
        </div>
    );
};

interface IFilterBarSingleSelectProps {
    label: string;
    items: IDropdownItem[];
    disabled: boolean;
    required?: boolean;
    searchable?: boolean;
    selectionChanged: (selectedItem: IDropdownItem | null) => void;
    selectedValue: any;
    isMobileView: boolean;
    sort?: boolean;
    sortItemsByValue?: boolean;
    placeholder?: string;
    hideLabel?: boolean;
    hideSearchAdornments?: boolean;
    inputHeight?: number;
    selectFontSize?: number;
    optionsFontSize?: number;
    selectIcon?: 'angle' | 'caret';
    clearable?: boolean;
}

const SingleSelect = (props: IFilterBarSingleSelectProps) => {
    const initialSelectedItem = props.selectedValue ? props.items.find((i) => i.value === props.selectedValue) || null : null;

    const [selectedItem, setSelectedItem] = useState<IDropdownItem | null>(initialSelectedItem);
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownHasFocus, setDropdownHasFocus] = useState(false);
    const [searchFilter, setSearchFilter] = useState('');
    const [dropdownItems, setDropdownItems] = useState<IDropdownItem[]>([]);
    const [filteredDropdownItems, setFilteredDropdownItems] = useState<IDropdownItem[]>([]);

    const isFiltering = searchFilter.length > 0;

    const dropdownMenuProps: Partial<MenuProps> = {
        transformOrigin: {
            horizontal: props.isMobileView ? 'right' : 'left',
            vertical: 'top',
        },
        anchorOrigin: {
            horizontal: props.isMobileView ? 'right' : 'left',
            vertical: 'bottom',
        },
        disablePortal: true,
        disableScrollLock: true,
        disableAutoFocus: true,
        disableEnforceFocus: true,
        slotProps: {
            root: {
                sx: {
                    width: 'auto',
                },
            },
            paper: {
                sx: {
                    overflowY: 'hidden !important',
                    maxHeight: 400,
                    width: 'auto',
                    minWidth: 'max-content',
                },
            },
        },
    };

    useEffect(() => {
        const newSelectedItem =
            props.selectedValue !== null && props.selectedValue !== undefined
                ? props.items.find((i) => i.value === props.selectedValue) || null
                : null;
        setSelectedItem(newSelectedItem);
        setDropdownItems(props.items);
    }, [props.items, props.selectedValue]);

    useEffect(() => {
        setFilteredDropdownItems(
            props.searchable
                ? dropdownItems.filter(
                      (item) =>
                          item.label.toLowerCase().includes(searchFilter.toLowerCase()) ||
                          item.secondaryLabel?.toLowerCase().includes(searchFilter.toLowerCase()) ||
                          item.extraSearchTerms?.some((term) => term.toLowerCase().includes(searchFilter.toLowerCase())),
                  )
                : dropdownItems,
        );
    }, [searchFilter, dropdownItems]);

    const handleSearchFilterChange = (value: string) => {
        setSearchFilter(value);
    };

    const handleOpen = () => {
        setIsOpen(true);
        setSearchFilter('');
        setDropdownHasFocus(true);
    };

    const clearStates = () => {
        setIsOpen(false);
        setSearchFilter('');
        setDropdownHasFocus(false);
    };

    const handleClose = () => {
        clearStates();
    };

    const handleMenuItemClick = (item: IDropdownItem) => {
        setSelectedItem(item);
        props.selectionChanged(item);
        clearStates();
    };

    const handleClickAway = () => {
        if (isOpen) {
            handleClose();
        }
    };

    return (
        <>
            {!props.hideLabel && (
                <div className={isEncompass ? styles.formLabel : styles.formLabelGeotab}>
                    {props.label}
                    {props.required &&
                        (isEncompass ? (
                            <span className={styles.requiredLabel}>REQUIRED</span>
                        ) : (
                            <span style={{ fontSize: '14px' }}>*</span>
                        ))}
                </div>
            )}
            <FormControl fullWidth focused={dropdownHasFocus}>
                <Select
                    labelId={props.label + '-select-label'}
                    id={props.label + '-select'}
                    inputProps={{ 'aria-label': 'Without label', sx: { padding: '0 !important' } }}
                    displayEmpty
                    renderValue={() => (
                        <DropdownLabel
                            labelText={props.label}
                            selectedItem={selectedItem}
                            placeholder={props.placeholder ?? 'Select ...'}
                            selectFontSize={props.selectFontSize}
                        />
                    )}
                    value={selectedItem && dropdownItems.some((item) => item.value === selectedItem.value) ? selectedItem.value : ''}
                    disabled={props.disabled}
                    MenuProps={dropdownMenuProps}
                    IconComponent={() => (
                        <Box className={styles.selectIcon}>
                            {props.clearable && selectedItem && (
                                <Box
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedItem(null);
                                        props.selectionChanged(null);
                                    }}
                                    sx={{ cursor: 'pointer', lineHeight: 1 }}
                                >
                                    <FontAwesomeIcon icon={faTimes} color={scssStyles.videoEventFilterSelectIconColor} />
                                </Box>
                            )}
                            <Box onClick={props.disabled ? undefined : isOpen ? handleClose : handleOpen} sx={{ lineHeight: 1 }}>
                                <FontAwesomeIcon
                                    icon={
                                        props.selectIcon === 'caret' ? (isOpen ? faCaretUp : faCaretDown) : isOpen ? faAngleUp : faAngleDown
                                    }
                                    color={scssStyles.videoEventFilterSelectIconColor}
                                />
                            </Box>
                        </Box>
                    )}
                    open={isOpen}
                    onClose={() => handleClickAway()}
                    onOpen={handleOpen}
                    autoFocus={false}
                    sx={{
                        cursor: 'pointer',
                        height: props.inputHeight ? `${props.inputHeight}px` : isEncompass ? '32px' : '40px',
                        '&:focus': { backgroundColor: 'transparent' },
                        fieldset: {
                            border: dropdownHasFocus
                                ? isEncompass
                                    ? '1px solid #0D7AC5 !important'
                                    : '1px solid #25477B !important'
                                : isEncompass
                                  ? '1px solid #C4CBD4 !important'
                                  : '1px solid #C4CBD7 !important',
                        },
                    }}
                >
                    {props.searchable && (
                        <DropdownSearchField
                            onChange={handleSearchFilterChange}
                            searchFilter={searchFilter}
                            onSearchFieldFocus={() => setDropdownHasFocus(false)}
                            onSearchFieldFocusLost={() => setDropdownHasFocus(true)}
                            placeholder={isEncompass ? 'Enter Search Term...' : 'Search ...'}
                            hideAdornments={props.hideSearchAdornments && !isEncompass}
                        />
                    )}
                    <Box className={isEncompass ? styles.scrollAreaEncompass : styles.scrollAreaGeotab}>
                        {(props.sort === false
                            ? filteredDropdownItems || []
                            : (filteredDropdownItems || [])
                                  .slice()
                                  .sort((a, b) =>
                                      props.sortItemsByValue
                                          ? a.value - b.value
                                          : a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
                                  )
                        ).map((item, index) => (
                            <DropdownMenuItem
                                key={index}
                                value={item.value}
                                checked={selectedItem?.value === item.value}
                                label={item.label}
                                secondaryLabel={item.secondaryLabel}
                                onChange={() => handleMenuItemClick(item)}
                                searchFilter={searchFilter}
                                checkboxDisabled={true}
                                optionsFontSize={props.optionsFontSize}
                            />
                        ))}
                    </Box>
                    {isFiltering && filteredDropdownItems.length === 0 && <div className={styles.noResultsMessage}>No results found</div>}
                </Select>
            </FormControl>
        </>
    );
};

export default SingleSelect;
